import { mapFxCategorizationEvent } from '@/lib/mappers';
import { supabase } from '@/lib/supabase';
import {
  FxCategorizationEvent,
  FxCategorizationEventType,
  FxVendorAffinity,
  FxRuleEffectiveness,
} from '@/types/api/finance';
import { getActiveOrgId, applyOrgFilter } from '@/lib/activeOrg';

const financeCategorizationEventsService = {
  async logEvent(params: {
    transactionId: string;
    eventType: FxCategorizationEventType;
    prevVendorId?: string | null;
    prevCategoryId?: string | null;
    prevMatchSource?: string | null;
    newVendorId?: string | null;
    newCategoryId?: string | null;
    ruleId?: string | null;
    aiConfidence?: number | null;
    description?: string | null;
    amount?: number | null;
  }): Promise<FxCategorizationEvent> {
    const orgId = getActiveOrgId();
    const { data, error } = await supabase
      .from('fx_categorization_events')
      .insert({
        transaction_id: params.transactionId,
        event_type: params.eventType,
        prev_vendor_id: params.prevVendorId ?? null,
        prev_category_id: params.prevCategoryId ?? null,
        prev_match_source: params.prevMatchSource ?? null,
        new_vendor_id: params.newVendorId ?? null,
        new_category_id: params.newCategoryId ?? null,
        rule_id: params.ruleId ?? null,
        ai_confidence: params.aiConfidence ?? null,
        description: params.description ?? null,
        amount: params.amount ?? null,
        ...(orgId ? { organization_id: orgId } : {}),
      })
      .select('*')
      .single();

    if (error) throw error;
    return mapFxCategorizationEvent(data);
  },

  async bulkLogEvents(
    events: Array<{
      transactionId: string;
      eventType: FxCategorizationEventType;
      prevVendorId?: string | null;
      prevCategoryId?: string | null;
      prevMatchSource?: string | null;
      newVendorId?: string | null;
      newCategoryId?: string | null;
      ruleId?: string | null;
      aiConfidence?: number | null;
      description?: string | null;
      amount?: number | null;
    }>
  ): Promise<void> {
    if (events.length === 0) return;
    const orgId = getActiveOrgId();
    const rows = events.map((e) => ({
      transaction_id: e.transactionId,
      event_type: e.eventType,
      prev_vendor_id: e.prevVendorId ?? null,
      prev_category_id: e.prevCategoryId ?? null,
      prev_match_source: e.prevMatchSource ?? null,
      new_vendor_id: e.newVendorId ?? null,
      new_category_id: e.newCategoryId ?? null,
      rule_id: e.ruleId ?? null,
      ai_confidence: e.aiConfidence ?? null,
      description: e.description ?? null,
      amount: e.amount ?? null,
      ...(orgId ? { organization_id: orgId } : {}),
    }));

    const { error } = await supabase.from('fx_categorization_events').insert(rows);
    if (error) throw error;
  },

  async getVendorAffinity(vendorId: string): Promise<FxVendorAffinity[]> {
    let query = supabase
      .from('fx_categorization_events')
      .select('new_category_id, fx_categories:new_category_id(name)')
      .eq('new_vendor_id', vendorId)
      .not('new_category_id', 'is', null);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;

    const counts: Record<string, { name: string; count: number }> = {};
    let total = 0;
    (data ?? []).forEach((row: any) => {
      const catId = row.new_category_id;
      const catName = row.fx_categories?.name ?? 'Unknown';
      if (!counts[catId]) counts[catId] = { name: catName, count: 0 };
      counts[catId].count += 1;
      total += 1;
    });

    return Object.entries(counts)
      .map(([categoryId, { name, count }]) => ({
        categoryId,
        categoryName: name,
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);
  },

  async getRuleEffectiveness(): Promise<FxRuleEffectiveness[]> {
    let query = supabase
      .from('fx_categorization_rules')
      .select('id, match_pattern, hit_count, override_count')
      .eq('is_active', true)
      .order('hit_count', { ascending: false });
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;

    return (data ?? []).map((row: any) => {
      const hits = row.hit_count ?? 0;
      const overrides = row.override_count ?? 0;
      const total = hits + overrides;
      return {
        ruleId: row.id,
        matchPattern: row.match_pattern,
        accuracy: total > 0 ? Math.round((hits / total) * 100) : 100,
        hitCount: hits,
        overrideCount: overrides,
      };
    });
  },

  async getRecentOverrides(limit = 50): Promise<FxCategorizationEvent[]> {
    let query = supabase
      .from('fx_categorization_events')
      .select('*')
      .in('event_type', ['manual_edit', 'import_overridden'])
      .order('created_at', { ascending: false })
      .limit(limit);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;
    return (data ?? []).map(mapFxCategorizationEvent);
  },

  async getOverrideCountThisMonth(): Promise<number> {
    const now = new Date();
    const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

    let query = supabase
      .from('fx_categorization_events')
      .select('id', { count: 'exact', head: true })
      .in('event_type', ['manual_edit', 'import_overridden'])
      .gte('created_at', monthStart);
    query = applyOrgFilter(query);
    const { count, error } = await query;

    if (error) throw error;
    return count ?? 0;
  },
};

export default financeCategorizationEventsService;
